
import { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import { Link } from "react-router-dom";
import { CalendarDays, ArrowRight } from "lucide-react";

import "swiper/css";

import { AuthAPI } from "../api/AuthAPI";

export default function BlogSection() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadBlogs();
  }, []);

  async function loadBlogs() {
    try {
      setLoading(true);

      const res = await AuthAPI.getAllBlogs({
        page: 1,
        limit: 6,
      });

      setBlogs(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load blogs", err);
    } finally {
      setLoading(false);
    }
  }

  if (loading || blogs.length === 0) {
    return null;
  }

  return (
    <section
      className="
        relative
        overflow-hidden
        py-16 md:py-24
        bg-gradient-to-b
        from-[#fff8fc]
        via-white
        to-[#f9fbff]
        font-poppins
      "
    >
      {/* BG BLUR */}

      <div
        className="
          absolute
          top-0
          left-0
          w-72
          h-72
          bg-pink-100/40
          rounded-full
          blur-3xl
        "
      />

      <div
        className="
          absolute
          bottom-0
          right-0
          w-72
          h-72
          bg-blue-100/40
          rounded-full
          blur-3xl
        "
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* ================= HEADING ================= */}

        <div className="text-center mb-12 md:mb-16">
          {/* TAG */}

          <div
            className="
              inline-flex
              items-center

              px-5
              py-2

              rounded-full

              bg-pink-100
              text-pink-600

              text-xs
              md:text-sm

              font-semibold
              tracking-[2px]
              uppercase

              mb-5
            "
          >
            Latest Articles
          </div>

          {/* TITLE */}

          <h2
            className="
              text-[34px]
              sm:text-[42px]
              md:text-[56px]

              leading-[1.05]

              font-bold

              text-[#1A237E]

              mb-5
            "
          >
            Discover Our
            <span className="text-pink-500"> Blog</span>
          </h2>

          {/* DESC */}

          <p
            className="
              max-w-2xl
              mx-auto

              text-gray-500

              text-[15px]
              md:text-[17px]

              leading-[1.9]
            "
          >
            Explore wellness tips, hygiene awareness and expert guidance crafted
            to help women feel confident, comfortable and cared for every day.
          </p>
        </div>

        {/* ================= SWIPER ================= */}

        <Swiper
          modules={[Autoplay]}
          autoplay={{
            delay: 4500,
            disableOnInteraction: false,
          }}
          loop={true}
          spaceBetween={24}
          breakpoints={{
            0: {
              slidesPerView: 1.1,
            },

            640: {
              slidesPerView: 2,
            },

            1024: {
              slidesPerView: 3,
            },
          }}
        >
          {blogs.map((blog) => (
            <SwiperSlide key={blog.id}>
              <article
                className="
                  group

                  h-full

                  bg-white/90
                  backdrop-blur-lg

                  rounded-[30px]

                  overflow-hidden

                  border
                  border-pink-100

                  shadow-[0_10px_40px_rgba(236,72,153,0.06)]

                  transition-all
                  duration-500

                  hover:-translate-y-2
                  hover:shadow-[0_20px_60px_rgba(236,72,153,0.12)]
                "
              >
                {/* ================= IMAGE ================= */}

                <Link
                  to={`/blog/${blog.id}`}
                  className="
                    relative
                    block
                    overflow-hidden
                  "
                >
                  <div className="overflow-hidden">
                    <img
                      src={blog.image}
                      alt={blog.title}
                      className="
                        w-full
                        h-[230px]
                        sm:h-[250px]

                        object-cover

                        transition-transform
                        duration-700

                        group-hover:scale-110
                      "
                    />
                  </div>

                  {/* OVERLAY */}

                  <div
                    className="
                      absolute
                      inset-0

                      bg-gradient-to-t
                      from-black/30
                      via-transparent
                      to-transparent
                    "
                  />
                </Link>

                {/* ================= CONTENT ================= */}

                <div className="p-6 flex flex-col">
                  {/* DATE */}

                  <div
                    className="
                      flex
                      items-center
                      gap-2

                      text-pink-500

                      text-sm
                      font-medium

                      mb-4
                    "
                  >
                    <CalendarDays size={16} />
{new Date(blog.createdAt).toDateString()}
                  </div>

                  {/* TITLE */}

                  <h3
                    className="
                      text-[22px]

                      font-bold

                      text-[#1A237E]

                      leading-[1.4]

                      mb-4

                      transition-colors
                      duration-300

                      group-hover:text-pink-500
                    "
                  >
                    <Link to={`/blog/${blog.id}`}>{blog.title}</Link>
                  </h3>

                  {/* CONTENT */}

                  <p
                    className="
                      text-gray-500

                      text-[15px]

                      leading-[1.9]

                      mb-6

                      line-clamp-3
                    "
                  >
                    {blog.content?.replace(/<[^>]+>/g, "")}
                  </p>

                  {/* BUTTON */}

                  <div className="mt-auto">
                    <Link
                      to={`/blog/${blog.id}`}
                      className="
                        inline-flex
                        items-center
                        gap-2

                        text-[#1A237E]
                        font-semibold

                        transition-all
                        duration-300

                        group-hover:text-pink-500
                      "
                    >
                      Read Article
                      <ArrowRight
                        size={18}
                        className="
                          transition-transform
                          duration-300

                          group-hover:translate-x-1
                        "
                      />
                    </Link>
                  </div>
                </div>
              </article>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* ================= CTA BUTTON ================= */}

        <div className="flex justify-center mt-14">
          <Link
            to="/blog"
            className="
              inline-flex
              items-center
              gap-3

              px-8
              py-4

              rounded-full

              bg-gradient-to-r
              from-[#1A237E]
              to-[#EC4899]

              text-white
              font-semibold

              shadow-[0_12px_35px_rgba(91,45,168,0.18)]

              transition-all
              duration-300

              hover:scale-105
            "
          >
            Explore All Blogs
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
}